'use client'

import { useState, useEffect, useRef } from 'react'
import { signIn } from '@/lib/auth/auth-client'

type Step = 'idle' | 'loading' | 'waiting' | 'error'

function generateRequestId(): string {
  return crypto.randomUUID()
}

export default function LoginForm() {
  const [email, setEmail] = useState('')
  const [step, setStep] = useState<Step>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const requestIdRef = useRef<string>(generateRequestId())
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Polling: parte quando step = 'waiting'
  useEffect(() => {
    if (step !== 'waiting') {
      if (pollRef.current) clearInterval(pollRef.current)
      return
    }

    const requestId = requestIdRef.current

    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/auth/device-poll?requestId=${requestId}`)
        if (res.status === 200) {
          clearInterval(pollRef.current!)
          const data = await res.json()
          // Naviga verso l'endpoint che imposta il cookie E fa redirect in un'unica risposta HTTP
          window.location.href = `/api/auth/device-activate?t=${encodeURIComponent(data.token)}`
        }
      } catch {
        // ignora errori di rete transitori
      }
    }, 2500)

    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [step])

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) return
    setStep('loading')
    // Genera un nuovo requestId ad ogni tentativo
    requestIdRef.current = generateRequestId()
    try {
      await signIn.magicLink({
        email: email.trim(),
        callbackURL: `/auth/device/complete?requestId=${requestIdRef.current}`,
      })
      setStep('waiting')
    } catch (err) {
      console.error(err)
      setErrorMsg("Errore durante l'invio del link. Riprova.")
      setStep('error')
    }
  }

  async function handleGoogle() {
    setStep('loading')
    try {
      await signIn.social({ provider: 'google', callbackURL: '/admin' })
    } catch (err) {
      console.error(err)
      setErrorMsg('Errore durante il login con Google.')
      setStep('error')
    }
  }

  async function handleFacebook() {
    setStep('loading')
    try {
      await signIn.social({ provider: 'facebook', callbackURL: '/admin' })
    } catch (err) {
      console.error(err)
      setErrorMsg('Errore durante il login con Facebook.')
      setStep('error')
    }
  }

  if (step === 'waiting') {
    return (
      <div className="text-center py-6">
        {/* Spinner */}
        <div className="flex justify-center mb-5">
          <div className="w-12 h-12 rounded-full border-4 border-[#242164]/15 border-t-[#FE7200] animate-spin" />
        </div>

        <p className="text-xl font-display font-bold text-[#242164] mb-2">
          In attesa di conferma
        </p>
        <p className="text-[#676593] text-sm mb-1">
          Abbiamo inviato un link a <strong>{email}</strong>.
        </p>
        <p className="text-[#676593] text-sm mb-6">
          Apri l'email — anche dal telefono — e clicca <strong>Accedi ora</strong>.
          Questa pagina si aggiornerà automaticamente.
        </p>

        <div className="bg-[#f8f8fc] border border-[#242164]/10 rounded-xl px-5 py-4 text-left text-xs text-gray-500 leading-relaxed mb-6">
          <span className="font-bold text-[#242164] block mb-1">Come funziona</span>
          Puoi aprire il link su qualsiasi dispositivo — anche dal cellulare.
          L'accesso verrà attivato automaticamente su <strong>questo browser</strong>.
        </div>

        <button
          type="button"
          onClick={() => { setStep('idle'); setEmail('') }}
          className="text-sm text-[#FE7200] underline"
        >
          Usa un altro indirizzo
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Magic link */}
      <form onSubmit={handleMagicLink} className="space-y-3">
        <label className="block text-sm font-medium text-[#242164]">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="nome@esempio.it"
          className="w-full border border-black/15 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FE7200] bg-white"
        />
        <button
          type="submit"
          disabled={step === 'loading'}
          className="w-full bg-[#FE7200] text-white font-display font-bold uppercase tracking-wide rounded-lg py-3 px-6 hover:bg-[#e56600] transition-colors disabled:opacity-50"
        >
          {step === 'loading' ? 'Invio in corso…' : 'Accedi con Magic Link'}
        </button>
      </form>

      <div className="flex items-center gap-3">
        <hr className="flex-1 border-black/10" />
        <span className="text-xs text-[#676593] uppercase tracking-widest">oppure</span>
        <hr className="flex-1 border-black/10" />
      </div>

      <div className="space-y-3">
        <button
          type="button"
          onClick={handleGoogle}
          disabled={step === 'loading'}
          className="w-full flex items-center justify-center gap-3 border border-black/15 rounded-lg py-3 px-5 text-sm font-medium text-[#242164] hover:bg-black/5 transition-colors disabled:opacity-50"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continua con Google
        </button>

        <button
          type="button"
          onClick={handleFacebook}
          disabled={step === 'loading'}
          className="w-full flex items-center justify-center gap-3 border border-black/15 rounded-lg py-3 px-5 text-sm font-medium text-[#242164] hover:bg-black/5 transition-colors disabled:opacity-50"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          Continua con Facebook
        </button>
      </div>

      {step === 'error' && (
        <p className="text-sm text-red-600 text-center">{errorMsg}</p>
      )}

      <p className="text-xs text-center text-[#676593]">
        Non hai un account? Registrati con la tua email e attendi l'approvazione dell'admin.
      </p>
    </div>
  )
}
